<script lang="ts" setup>
import {cn} from '@/lib/utils';
import {CalendarRoot, type CalendarRootEmits, type CalendarRootProps, useForwardPropsEmits} from 'reka-ui';
import {type HTMLAttributes, ref} from 'vue';
import {
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHead,
    CalendarGridRow,
    CalendarHeadCell,
    CalendarHeader,
    CalendarHeading,
    CalendarNextButton,
    CalendarPrevButton
} from '@/core/components/ui/calendar';
import {DateValue, getLocalTimeZone} from "@internationalized/date";
import EventCalendarCell
    from "@/appointment/components/consumer-appointment-timeline/event-calendar/EventCalendarCell.vue";

export interface CalendarEvent {
    id: string | number;
    date: Date | string;
    title: string;
    time?: string;
    color?: string;
    category?: string;
    description?: string;
    /** Names resolved by the producer, so the tooltip stays shape-agnostic. */
    patientName?: string | null;
    operatorName?: string | null;
}

const {events = [], class: componentClass, ...delegatedProps} = defineProps<CalendarRootProps & {
    class?: HTMLAttributes['class'];
    events?: CalendarEvent[];
}>();

const emits = defineEmits<CalendarRootEmits & {
    'date-range-changed': [range: { startDate: Date | null; endDate: Date | null }];
}>();
const currentGrid = ref();

const forwarded = useForwardPropsEmits(delegatedProps, emits);

/**
 * Get the calendar events for a specific date.
 */
const getEventsForDate = (date: DateValue): CalendarEvent[] => {
    if (!events?.length) return [];

    return events.filter(event => {
        const eventDate = new Date(event.date);

        return eventDate.toDateString() === date.toDate(getLocalTimeZone()).toDateString();
    });
};

/**
 * Get the calendar date range.
 */
const getCalendarDateRange = (grid: any[]) => {
    if (!grid || grid.length === 0) {
        return {startDate: null, endDate: null};
    }

    const firstMonth = grid[0];
    const startDate = firstMonth.rows[0]?.[0] as DateValue;

    const lastMonth = grid[grid.length - 1];
    const lastRow = lastMonth.rows[lastMonth.rows.length - 1];
    const endDate = lastRow?.[lastRow.length - 1] as DateValue;

    return {
        startDate: startDate?.toDate(getLocalTimeZone()) || null,
        endDate: endDate?.toDate(getLocalTimeZone()) || null
    };
};

const handleGridUpdate = (grid) => {
    const newRange = getCalendarDateRange(grid);
    const oldRange = getCalendarDateRange(currentGrid.value);

    if (newRange.startDate?.getTime() !== oldRange.startDate?.getTime() ||
        newRange.endDate?.getTime() !== oldRange.endDate?.getTime()) {
        currentGrid.value = grid;
        emits('date-range-changed', newRange);
    }
};
</script>

<template>
  <CalendarRoot
    v-slot="{ grid, weekDays }"
    :class="cn('p-3', componentClass)"
    v-bind="forwarded"
    locale="fr-FR"
  >
    <div style="display: none;">
      {{ handleGridUpdate(grid) }}
    </div>
    <CalendarHeader>
      <CalendarPrevButton />
      <CalendarHeading />
      <CalendarNextButton />
    </CalendarHeader>

    <div class="flex flex-col gap-y-4 mt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <CalendarGrid v-for="month in grid" :key="month.value.toString()">
        <CalendarGridHead>
          <CalendarGridRow>
            <CalendarHeadCell
              v-for="day in weekDays"
              :key="day" class="flex-1"
            >
              {{ day }}
            </CalendarHeadCell>
          </CalendarGridRow>
        </CalendarGridHead>
        <CalendarGridBody>
          <CalendarGridRow v-for="(weekDates, index) in month.rows" :key="`weekDate-${index}`" class="w-full">
            <EventCalendarCell
              v-for="date in weekDates as DateValue[]"
              :key="date.toString()"
              :date="date"
              :month="month"
              :events="getEventsForDate(date)"
            />
          </CalendarGridRow>
        </CalendarGridBody>
      </CalendarGrid>
    </div>
  </CalendarRoot>
</template>
